// ────────────────────────────────────────────────────────────
// DEALS STORE
// ────────────────────────────────────────────────────────────

import { create } from 'zustand';
import {
  dealsRepo, filesRepo, titleRepo, overviewRepo, compsRepo, leadHistoryRepo,
  packageRepo, buyersRepo, activityRepo, loadDealBundle,
} from '../services/persistence';
import type {
  Deal, DealFile, TitleFinding, ChainLink, MathWorksheet, TitleFlag,
  OwnershipPicture, Tract, Comp, LeadEvent, PackageDraft,
  BuyerProspect, BuyerMatch, ActivityEvent,
  InvestigationStage, WorkingTheory, OutstandingResearch, DeadEnd,
} from '../types';
import { entitiesApi } from '../services/api';

interface PerDealData {
  files: DealFile[];
  findings: TitleFinding[];
  chain: ChainLink[];
  math?: MathWorksheet;
  flags: TitleFlag[];
  picture?: OwnershipPicture;
  tracts: Tract[];
  comps: Comp[];
  leadEvents: LeadEvent[];
  packageDraft?: PackageDraft;
  buyerMatches: BuyerMatch[];
  activity: ActivityEvent[];
  // Day 2.7 — investigator
  investigationStages: InvestigationStage[];
  workingTheory?: WorkingTheory;
  outstandingResearch: OutstandingResearch[];
  deadEnds: DeadEnd[];
  loaded: boolean;
}

interface DealsState {
  deals: Deal[];
  byDealId: Record<string, PerDealData>;
  buyers: BuyerProspect[];
  loadAllDeals: () => Promise<void>;
  loadDealData: (dealId: string) => Promise<void>;
  setBuyers: (buyers: BuyerProspect[]) => void;
  saveDeal: (d: Deal) => Promise<void>;
  saveFile: (f: DealFile) => Promise<void>;
  saveFinding: (f: TitleFinding) => Promise<void>;
  saveChainLink: (c: ChainLink) => Promise<void>;
  saveMath: (m: MathWorksheet) => Promise<void>;
  saveFlag: (f: TitleFlag) => Promise<void>;
  savePicture: (p: OwnershipPicture) => Promise<void>;
  saveTract: (t: Tract) => Promise<void>;
  saveComp: (c: Comp) => Promise<void>;
  saveLeadEvent: (e: LeadEvent) => Promise<void>;
  savePackage: (p: PackageDraft) => Promise<void>;
  saveBuyerMatches: (dealId: string, matches: BuyerMatch[]) => Promise<void>;
  appendActivity: (e: ActivityEvent) => Promise<void>;
  // Day 2.7
  resolveResearch: (item: OutstandingResearch, note?: string) => Promise<void>;
}

const EMPTY_DATA: PerDealData = Object.freeze({
  files: [], findings: [], chain: [], flags: [], tracts: [],
  comps: [], leadEvents: [], buyerMatches: [], activity: [],
  investigationStages: [], outstandingResearch: [], deadEnds: [],
  loaded: false,
}) as PerDealData;

export const useDealsStore = create<DealsState>((set, get) => ({
  deals: [],
  byDealId: {},
  buyers: [],

  async loadAllDeals() {
    const deals = await dealsRepo.list();
    set({ deals });
  },

  async loadDealData(dealId) {
    if (get().byDealId[dealId]?.loaded) return;
    try {
      const bundle = await loadDealBundle(dealId);
      // Update the deal record from the bundle if it changed
      const existingDeals = get().deals;
      const dealsUpdated = existingDeals.some(d => d.id === bundle.deal.id)
        ? existingDeals.map(d => d.id === bundle.deal.id ? bundle.deal : d)
        : [bundle.deal, ...existingDeals];

      set(state => ({
        deals: dealsUpdated,
        byDealId: {
          ...state.byDealId,
          [dealId]: {
            files: bundle.files, findings: bundle.findings, chain: bundle.chain,
            math: bundle.math, flags: bundle.flags, picture: bundle.picture,
            tracts: bundle.tracts, comps: bundle.comps,
            leadEvents: bundle.leadEvents, packageDraft: bundle.packageDraft,
            buyerMatches: bundle.buyerMatches, activity: bundle.activity,
            investigationStages: bundle.investigationStages || [],
            workingTheory: bundle.workingTheory,
            outstandingResearch: bundle.outstandingResearch || [],
            deadEnds: bundle.deadEnds || [],
            loaded: true,
          },
        },
      }));
      // The conversation comes back in the bundle too — push it into the conversation store
      const { useConversationStore } = await import('./conversation');
      useConversationStore.getState().setMessages(dealId, bundle.conversation);
    } catch (e) {
      console.error('loadDealData failed', e);
    }
  },

  setBuyers(buyers) { set({ buyers }); },

  async saveDeal(d) {
    await dealsRepo.save(d);
    set(state => ({ deals: state.deals.some(x => x.id === d.id)
      ? state.deals.map(x => x.id === d.id ? d : x)
      : [d, ...state.deals],
    }));
  },

  async saveFile(f) {
    await filesRepo.save(f);
    const data = get().byDealId[f.dealId] ?? { ...EMPTY_DATA };
    const files = [f, ...data.files.filter(x => x.id !== f.id)];
    set(state => ({ byDealId: { ...state.byDealId, [f.dealId]: { ...data, files } } }));
  },

  async saveFinding(f) {
    await titleRepo.saveFinding(f);
    const data = get().byDealId[f.dealId] ?? { ...EMPTY_DATA };
    const findings = [...data.findings.filter(x => x.id !== f.id), f]
      .sort((a, b) => a.date.localeCompare(b.date));
    set(state => ({ byDealId: { ...state.byDealId, [f.dealId]: { ...data, findings } } }));
  },

  async saveChainLink(c) {
    await titleRepo.saveChainLink(c);
    const data = get().byDealId[c.dealId] ?? { ...EMPTY_DATA };
    const chain = [...data.chain.filter(x => x.id !== c.id), c].sort((a, b) => a.ordinal - b.ordinal);
    set(state => ({ byDealId: { ...state.byDealId, [c.dealId]: { ...data, chain } } }));
  },

  async saveMath(m) {
    await titleRepo.saveMath(m);
    const data = get().byDealId[m.dealId] ?? { ...EMPTY_DATA };
    set(state => ({ byDealId: { ...state.byDealId, [m.dealId]: { ...data, math: m } } }));
  },

  async saveFlag(f) {
    await titleRepo.saveFlag(f);
    const data = get().byDealId[f.dealId] ?? { ...EMPTY_DATA };
    const flags = [...data.flags.filter(x => x.id !== f.id), f];
    set(state => ({ byDealId: { ...state.byDealId, [f.dealId]: { ...data, flags } } }));
  },

  async savePicture(p) {
    await overviewRepo.savePicture(p);
    const data = get().byDealId[p.dealId] ?? { ...EMPTY_DATA };
    set(state => ({ byDealId: { ...state.byDealId, [p.dealId]: { ...data, picture: p } } }));
  },

  async saveTract(t) {
    await overviewRepo.saveTract(t);
    const data = get().byDealId[t.dealId] ?? { ...EMPTY_DATA };
    const tracts = [...data.tracts.filter(x => x.id !== t.id), t];
    set(state => ({ byDealId: { ...state.byDealId, [t.dealId]: { ...data, tracts } } }));
  },

  async saveComp(c) {
    await compsRepo.save(c);
    const data = get().byDealId[c.dealId] ?? { ...EMPTY_DATA };
    const comps = [...data.comps.filter(x => x.id !== c.id), c]
      .sort((a, b) => b.date.localeCompare(a.date));
    set(state => ({ byDealId: { ...state.byDealId, [c.dealId]: { ...data, comps } } }));
  },

  async saveLeadEvent(e) {
    await leadHistoryRepo.save(e);
    const data = get().byDealId[e.dealId] ?? { ...EMPTY_DATA };
    const leadEvents = [e, ...data.leadEvents.filter(x => x.id !== e.id)]
      .sort((a, b) => b.occurredAt.localeCompare(a.occurredAt));
    set(state => ({ byDealId: { ...state.byDealId, [e.dealId]: { ...data, leadEvents } } }));
  },

  async savePackage(p) {
    await packageRepo.save(p);
    const data = get().byDealId[p.dealId] ?? { ...EMPTY_DATA };
    set(state => ({ byDealId: { ...state.byDealId, [p.dealId]: { ...data, packageDraft: p } } }));
  },

  async saveBuyerMatches(dealId, matches) {
    await buyersRepo.saveMatches(dealId, matches);
    const data = get().byDealId[dealId] ?? { ...EMPTY_DATA };
    set(state => ({ byDealId: { ...state.byDealId, [dealId]: { ...data, buyerMatches: matches } } }));
  },

  async appendActivity(e) {
    await activityRepo.append(e);
    const data = get().byDealId[e.dealId] ?? { ...EMPTY_DATA };
    const activity = [e, ...data.activity];
    set(state => ({ byDealId: { ...state.byDealId, [e.dealId]: { ...data, activity } } }));
  },

  async resolveResearch(item, note) {
    const updated: OutstandingResearch = {
      ...item,
      resolved: true,
      resolvedNote: note,
    };
    await entitiesApi.upsert(item.dealId, 'research', updated);
    const data = get().byDealId[item.dealId] ?? { ...EMPTY_DATA };
    const outstandingResearch = data.outstandingResearch.map(r =>
      r.id === item.id ? updated : r,
    );
    set(state => ({
      byDealId: { ...state.byDealId, [item.dealId]: { ...data, outstandingResearch } },
    }));
  },
}));

export function useDealData(dealId: string | null): PerDealData {
  return useDealsStore(s => (dealId ? (s.byDealId[dealId] ?? EMPTY_DATA) : EMPTY_DATA));
}
